import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useOnline } from './useOnline';
import { buildLabel, isUpdateInFlight, phaseLabel } from './versionCompare';
import type { ApplyUpdateOptions, ApplyUpdatePhase, VersionInfo } from './types';

interface ForcedUpdateModalProps {
  current: VersionInfo;
  latest: VersionInfo;
  onUpdate: (options?: ApplyUpdateOptions) => Promise<ApplyUpdatePhase>;
}

// Brand-compliant classes mirroring the shared <Button> primary variant.
// Hand-rolled (not imported) so this shared chrome stays portable across the CRA
// app and the three strict Vite PWAs without dragging client design components
// into their (verbatimModuleSyntax / noUnusedLocals) tsc programs.
const PRIMARY_BTN =
  'mt-5 w-full rounded-xl bg-[#007ACC] px-4 py-2.5 text-sm font-medium text-white ' +
  'transition-colors hover:bg-[#005fa3] active:bg-[#004a80] focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[#007ACC]/50 disabled:opacity-50 disabled:cursor-not-allowed';

/** Inline spinner so the loading state carries motion without imports. */
const SPINNER =
  'mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 ' +
  'border-white/40 border-t-white align-middle';

/**
 * Forced update modal for large drift. Not dismissable (no Escape, no backdrop
 * close) — only "Update now". Announced as an alertdialog, focus-trapped.
 *
 * The update itself can take minutes on a slow connection (the PWAs precache
 * their offline shell), so the button narrates the real phases instead of
 * pretending a spinner means seconds. While a run is in flight, its phase
 * outranks a transient offline blip — the install resumes on its own, and the
 * one message that must survive a blip is "keep the app open". On `failed`
 * (install rejected — bad connection, mid-flight drop) the button re-arms for
 * an explicit retry rather than silently reload-looping back into this modal.
 */
export function ForcedUpdateModal({
  current,
  latest,
  onUpdate,
}: ForcedUpdateModalProps): ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<ApplyUpdatePhase | null>(null);
  const online = useOnline();

  useEffect(() => {
    const focusButton = () => panelRef.current?.querySelector('button')?.focus();
    focusButton();
    // Trap focus on the single actionable control; swallow Escape.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        focusButton();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const busy = isUpdateInFlight(phase);

  const handleUpdate = () => {
    if (busy) return;
    void onUpdate({ onPhase: setPhase });
  };

  const buttonText = busy
    ? phaseLabel(phase)
    : !online
      ? 'Waiting for connection...'
      : phase === 'failed'
        ? phaseLabel(phase)
        : 'Update now';

  const helperText = busy
    ? 'Downloading in the background — this can take a few minutes on a slow connection. Keep the app open.'
    : !online
      ? 'You are offline. Reconnect to update.'
      : phase === 'failed'
        ? 'The download did not finish — check your connection and try again.'
        : 'If this keeps appearing after updating, contact support.';

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="forced-update-title"
      aria-describedby="forced-update-desc"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        className="w-full max-w-md rounded-xl border border-slate-300 bg-white p-6 shadow-xl"
      >
        <h2 id="forced-update-title" className="text-sm font-semibold text-slate-900">
          Update required
        </h2>
        <p id="forced-update-desc" className="mt-2 text-sm text-slate-600">
          This version of the app is no longer supported. Update to continue.
          <span className="mt-1 block text-slate-500 tabular-nums">
            {buildLabel(current)} &rarr; {buildLabel(latest)}
          </span>
        </p>
        <button
          type="button"
          onClick={handleUpdate}
          disabled={busy || !online}
          className={PRIMARY_BTN}
        >
          {busy && <span aria-hidden="true" className={SPINNER} />}
          {buttonText}
        </button>
        <p className="mt-3 text-xs text-slate-500" aria-live="polite">
          {helperText}
        </p>
      </div>
    </div>
  );
}
